import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Controlled as CodeMirror } from 'react-codemirror2';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import Moment from 'moment';
import {
    sortBy as _sortBy,
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Tooltip from '@mui/material/Tooltip';

import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';

import PolicyService from '../../../data/policy-service';
import ZeekService from '../../../data/zeek-service';
import DataContext from '../../../data/data-context';
import Auth from '../../../data/auth-service';

import Validator, { useValidate, checkValidity } from '../../../components/validator';
import Searchbar from '../../../components/searchbar';
import ConfirmationDialog from '../../../components/confirmationdlg';

import useWindowSize from '../../../libraries/usewindowsize';

const drawerWidth = '300px';
const drawerWidthCollapsed = '60px !important';

const useStyles = makeStyles(theme => ({
    content: {
        marginTop: '-20px',
        marginLeft: '-8px',
    },
    justifySpace: {
        justifyContent: 'space-between',
    },
    formControl: {
        margin: theme.spacing(1),
        width: '100%',
    },
    textfield: {
        width: '50%',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(1),
    },
    controlBar: {
        display: 'none',
    },
    header: {
        padding: '10px 16px',
        '& .MuiCardHeader-title': {
            fontSize: '16px',
        },
        '& .MuiCardHeader-subheader': {
            fontSize: '14px',
        }
    },
    gridOverflow: {
        maxHeight: window.innerHeight - 200,
        overflowY: 'scroll',
    },
    messageBox: {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.nav.paper,
        color: theme.palette.nav.text,
        border: `1px solid ${theme.palette.warning.main}`,
        marginBottom: '8px',
    },
    hideFileInput: {
        display: 'none',
    },
    drawer: {
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        backgroundColor: theme.palette.nav.paper,
        color: theme.palette.nav.text,
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
        transition: 'width 500ms',
    },
    drawerCollapsed: {
        width: drawerWidthCollapsed,
        transition: 'width 500ms',
    },
    drawerPaper: {
        width: drawerWidth,
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
        marginLeft: '60px',
        backgroundColor: theme.palette.nav.paper,
        color: theme.palette.nav.text,
        transition: 'margin-left 500ms, width 500ms',
    },
    drawerPaperExtended: {
        marginLeft: '300px',
        transition: 'margin-left 500ms, width 500ms',
    },
    listItem: {
        borderBottom: `1px solid ${theme.palette.common.white}`,
    },
    codeContainer: {
        // marginBottom: '-24px',
        '& .react-codemirror2': {
            height: '100%',
        },
        '& .CodeMirror': {
            height: '100%',
        }
    },
    editorButton: {
        marginTop: '8px',
        marginLeft: '8px'
    },
    selectedListItem: {
        background: 'rgba(100, 100, 100, 0.25)',
    },
    staticList: {
        // position: 'static',
        // top: 0,
    },
    scrollList: {
        height: '100%',
        overflowY: 'scroll',
    },
}));

const emptyScript = { name: '', folder: '', content: '' };

const SORT_TYPES = [
    { value: 'name', name: 'Name' },
    { value: 'folder', name: 'Library' },
    { value: 'author', name: 'Author' },
    { value: 'modified', name: 'Modified' },
    { value: 'ver', name: 'Version' },
];

const ZeekEditor = () => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const { systemSettings, sidenav, setSidenav } = useContext(DataContext);
    const [sortAnchor, setSortAnchor] = useState(null);
    const [policyType] = useState('bro_ti');
    const [scripts, setScripts] = useState([]);
    const [selectedScript, setSelectedScript] = useState({ ...emptyScript });
    const [original, setOriginal] = useState(_cloneDeep({ ...emptyScript }));
    const [edit, setEdit] = useState(false);
    const [writable, setWriteable] = useState(true);
    const [rename, setRename] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [sort, setSort] = useState('name+desc');
    const [editor, setEditor] = useState(null);
    const [init, setInit] = useState(false);
    const [changes, setChanges] = useState(false);
    const [disableHistory, setDisableHistory] = useState({ undo: false, redo: false });
    const [openCreate, setOpenCreate] = useState(false);
    const [isHeader, setIsHeader] = useState(false);
    const zeekScriptName = useValidate({});
    const { height } = useWindowSize();
    const zeekEditorPerms = Auth.permissions.get('ZeekScripts');

    useEffect(() => {
        if (systemSettings && systemSettings.ui) {
            setIsHeader(Boolean(systemSettings.ui.messages.header));
        }
    }, [systemSettings]);

    useEffect(() => {
        setSidenav({ sidebar: 'open' });

        return () => {
            setSidenav({ sidebar: undefined });
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (policyType) {
            getScripts();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [policyType, sort]);

    useEffect(() => {
        if (scripts.length && params.name && !init) {
            if (location && location.state && location.state.author) {
                handleSelectCard(scripts.findIndex(v => (v.name === params.name && v.author === location.state.author)));
            } else {
                handleSelectCard(scripts.findIndex(v => (v.name === params.name)));
            }
            setInit(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scripts]);

    useEffect(() => {
        if (edit && !writable) {
            setChanges(true);
        } else {
            setChanges(!_isEqual(selectedScript.content, original.content));
        }

        if (editor) {
            setDisableHistory({
                undo: !!editor.getHistory().done.length,
                redo: !!editor.getHistory().undone.length
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedScript.content, original.content]);

    const getScripts = (search) => {
        PolicyService.getPolicies(policyType).then(p => {
            ZeekService.getScriptsList((p.length ? p[0].name : 'default'), policyType, search).then(res => {
                res = res.map(r => { return { ...r, color: getColor() } });
                res = _sortBy(res, [sort.split('+')[0]]);
                if (sort.split('+')[1] === 'asc') {
                    res.reverse();
                }
                setScripts(res);
            });
        });
    };

    const getScript = (name, folder) => {
        return ZeekService.getScript(policyType, folder, name);
    };

    const handleUpdateScript = (editor, data, value) => {
        setSelectedScript({
            ...selectedScript,
            content: value,
        });
    };

    const handleDeleteScript = (e) => {
        ZeekService.deleteScript(policyType, encodeURIComponent(selectedScript.folder), encodeURIComponent(selectedScript.name + '.zeek')).then(res => {
            handleOpenDelete();
            getScripts();
            setEdit(false);
        });
    };

    const handleOpenDelete = (i) => {
        if (typeof i === 'number') {
            handleSelectCard(i).then(r => {
                setDeleteOpen(!deleteOpen);
            });
        } else {
            setDeleteOpen(!deleteOpen);
            setSelectedScript({ ...emptyScript });
            setEdit(false);
        }
    };

    const handleCreate = () => {
        if (edit && writable) {
            ZeekService.updateScript(
                policyType,
                encodeURIComponent(selectedScript.folder),
                encodeURIComponent(selectedScript.name),
                {
                    content: selectedScript.content,
                }
            ).then(res => {
                toast.success(`Script (${selectedScript.name}) updated successfully`);
                getScripts();
                setSelectedScript({
                    folder: selectedScript.folder,
                    name: selectedScript.name,
                    content: selectedScript.content,
                });
            }, err => {
                toast.error(`${err.message}: ${err.error}`);
            });
        } else if (edit && !writable) {
            ZeekService.createScript(policyType, selectedScript.name, selectedScript.content).then(res => {
                toast.success('Script duplicated successfully');
                getScripts();
                setSelectedScript({
                    name: selectedScript.name,
                    content: selectedScript.content,
                    folder: 'user_custom',
                });
                setEdit(true);
                setWriteable(true);
            }, err => {
                toast.error(`${err.message}: ${err.error}`);
            });
        } else {
            if (openCreate) {
                setOpenCreate(false);
                ZeekService.createScript(policyType, selectedScript.name, selectedScript.content).then(res => {
                    toast.success('Script created successfully');
                    getScripts();
                    setSelectedScript({
                        name: selectedScript.name,
                        content: selectedScript.content,
                        folder: 'user_custom',
                    });
                }, err => {
                    toast.error(`${err.message}: ${err.error}`);
                });
            } else {
                setOpenCreate(true);
                setRename(false);
            }
        }
    };

    const handleRename = () => {
        if (edit && writable) {
            ZeekService.updateScript(
                policyType,
                encodeURIComponent(selectedScript.folder),
                encodeURIComponent(params.name),
                {
                    name: selectedScript.name,
                    content: selectedScript.content
                }
            ).then(res => {
                toast.success(`Script (${selectedScript.name}) updated successfully`);
                getScripts();
                setSelectedScript({
                    folder: selectedScript.folder,
                    name: selectedScript.name,
                    content: selectedScript.content,
                });
                scriptNavigate(selectedScript.name);
            }, err => {
                toast.error(`${err.message}: ${err.error}`);
            });
        } else {
            toast.error('Unable to update script name');
        }
        setOpenCreate(false);
    };

    const handleStartRename = () => {
        setOpenCreate(true);
        setRename(true);
    };

    const handleCancel = () => {
        setEdit(false);
        setWriteable(true);
        setOriginal(_cloneDeep(emptyScript));
        setSelectedScript({ ...emptyScript });
    };

    const scriptNavigate = (name) => {
        let l = location.pathname.split('/');
        l.pop();
        if (!l.includes('editor')) {
            l.push('editor');
        }
        l = l.join('/');
        navigate(l + '/' + name, { replace: true, state: location.state });
    };

    const handleSelectCard = (i) => {
        if (scripts[i] && scripts[i].name) {
            return getScript(scripts[i].name, encodeURIComponent(scripts[i].folder)).then(s => {
                scriptNavigate(scripts[i].name);
                setEdit(true);
                setWriteable(scripts[i].is_rw);
                setSelectedScript({
                    ...scripts[i],
                    name: scripts[i].name.replace('.zeek', ''),
                    content: s.content,
                });
                setOriginal(_cloneDeep({
                    ...scripts[i],
                    name: scripts[i].name.replace('.zeek', ''),
                    content: s.content,
                }));
            });
        }
    };

    const handleSearchScripts = ({ search }) => {
        getScripts(search);
        if (!search) {
            setEdit(false);
            setWriteable(true);
            setSelectedScript({ ...emptyScript });
        }
    };

    const getColor = () => {
        const colors = [...theme.SeverityColors, ...theme.ChartColors];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    const handleGoBack = () => {
        if (location.state && location.state.policy) {
            navigate(`/policies/zeek/${location.state.bt ? 'bt_scripts' : 'scripts'}/${location.state.policy}`, {
                state: {
                    scripts: location.state.scripts,
                }
            });
            return;
        }
        navigate(-1);
    };

    const handleCloseSort = () => {
        setSortAnchor(null);
    };

    const codemirrorOptions = {
        lineNumbers: true,
        theme: theme.codeMirror.theme,
        mode: 'zeek',
        readOnly: !zeekEditorPerms.write,
    };

    const handleUpdateSort = (v) => {
        setSortAnchor(null);
        if (v === sort.split('+')[0]) {
            if (sort.split('+')[1] === 'desc') {
                setSort(v + '+asc');
                return;
            }
        }
        setSort(v + '+desc');
    };

    const handleUndo = () => {
        editor.undo();
    };

    const handleRedo = () => {
        editor.redo();
    };

    const handleUpdateScriptName = (e, validID, valid) => {
        zeekScriptName.set({ ...zeekScriptName.get(), [validID]: valid });

        setSelectedScript({
            ...selectedScript,
            name: e.target.value,
        });
    };

    const handleOpenCreate = () => {
        setOpenCreate(!openCreate);
    };

    const handleCancelChanges = () => {
        setSelectedScript(_cloneDeep(original));
    };

    return (
        <main className={classes.content}>
            <Drawer
                open={true}
                className={classes.drawer}
                variant="persistent"
                classes={{
                    paper: clsx({
                        [classes.drawerPaper]: true,
                        [classes.drawerPaperExtended]: !sidenav.collapsed,
                    }),
                }}
            >
                <Box style={{ marginTop: (isHeader ? 98 : 72) }}></Box>
                <List className={classes.staticList}>
                    <ListItem>
                        <Searchbar immediate onSearch={handleSearchScripts} searchservice={ZeekService} />
                    </ListItem>
                    <ListItem className={classes.listItem}>
                        <Button variant="contained" color="primary" onClick={(e) => { setSortAnchor(e.currentTarget) }}>
                            {SORT_TYPES[SORT_TYPES.findIndex(v => v.value === sort.split('+')[0])].name} {sort.split('+')[1] === 'desc' ? <ExpandMore /> : <ExpandLess />}
                        </Button>
                        <Menu
                            anchorEl={sortAnchor}
                            open={Boolean(sortAnchor)}
                            onClose={handleCloseSort}
                        >
                            {SORT_TYPES.map((v, i) => {
                                return (
                                    <MenuItem
                                        key={'sort-menu-item-' + i}
                                        className={classes.menuItem}
                                        onClick={() => { handleUpdateSort(v.value) }}
                                    >
                                        <Box display="flex" justifyContent="space-between" width="100%">
                                            {v.name}
                                            {sort.split('+')[0] === v.value ? (sort.split('+')[1] === 'desc' ? <ExpandMore /> : <ExpandLess />) : null}
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </Menu>
                    </ListItem>
                </List>
                <List className={classes.scrollList}>
                    {scripts.map((v, i) => {
                        return (
                            <ListItem key={'zeek-script-list-item-' + i}
                                className={clsx({
                                    [classes.listItem]: true,
                                    [classes.selectedListItem]: selectedScript.name === v.name.replace('.zeek', '') && selectedScript.folder === v.folder,
                                })} onClick={() => { handleSelectCard(i) }}
                                style={{ cursor: 'pointer', padding: '5px 18px' }}
                            >
                                <ListItemText
                                    disableTypography
                                    primary={<Typography variant="body2" component="p">
                                        <Tooltip title={v.purpose || ''} style={{ cursor: 'pointer', padding: '5px 0px' }}>
                                            <span>
                                                {v.name} ({v.ver})
                                            </span>
                                        </Tooltip>
                                    </Typography>}
                                    secondary={<Typography variant="caption" style={{ color: '#AAA' }}>{`${v.author} - ${Moment(v.time).format(theme.date.long)}`}</Typography>}
                                />
                                {zeekEditorPerms.delete && v.is_rw && <ListItemSecondaryAction>
                                    <IconButton color="secondary" onClick={() => { handleOpenDelete(i) }} size="large">
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>}
                            </ListItem>
                        );
                    })}
                    <ListItem style={{ height: '50px' }}></ListItem>
                </List>
            </Drawer>
            <Box className={classes.editor}>
                <Box display="flex" justifyContent="space-between" my={1}>
                    <Box flexGrow={1}>
                        <Tooltip title="Cancel changes">
                            <span>
                                <Button
                                    className={classes.editorButton}
                                    variant="outlined"
                                    aria-label="cancel edits"
                                    onClick={handleCancelChanges}
                                    disabled={!writable || !zeekEditorPerms.write}
                                >Cancel</Button>
                            </span>
                        </Tooltip>
                        <Tooltip title="Undo (ctrl+z)">
                            <span>
                                <Button
                                    className={classes.editorButton}
                                    variant="outlined"
                                    aria-label="undo"
                                    onClick={handleUndo}
                                    disabled={!disableHistory.undo || !zeekEditorPerms.write}
                                >
                                    <UndoIcon />
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title="Redo (ctrl+y)">
                            <span>
                                <Button
                                    className={classes.editorButton}
                                    variant="outlined"
                                    aria-label="redo"
                                    onClick={handleRedo}
                                    disabled={!disableHistory.redo || !zeekEditorPerms.write}
                                >
                                    <RedoIcon />
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title={`${edit ? (writable ? 'Update' : 'Duplicate') : 'Create'} a script`}>
                            <span>
                                <Button
                                    className={classes.editorButton}
                                    variant="outlined"
                                    aria-label={edit ? (writable ? 'Update' : 'Duplicate') : 'Create'}
                                    onClick={handleCreate}
                                    disabled={!changes || !zeekEditorPerms.write}
                                >
                                    {edit ? (writable ? 'Update' : 'Duplicate') : 'Create'}
                                </Button>
                            </span>
                        </Tooltip>
                        {writable && edit && <Tooltip title="Rename">
                            <span>
                                <Button
                                    className={classes.editorButton}
                                    variant="outlined"
                                    aria-label="Rename"
                                    onClick={handleStartRename}
                                    disabled={!zeekEditorPerms.write}
                                >
                                    Rename
                                </Button>
                            </span>
                        </Tooltip>}
                    </Box>
                    <Box display="flex">
                        <Box mr={1}>
                            <a target="_blank" rel="noreferrer noopener" href="https://docs.zeek.org/en/current/examples/scripting/index.html">
                                <IconButton size="large"><InfoIcon /></IconButton>
                            </a>
                        </Box>
                        <Box mr={1}>
                            <Button
                                className={classes.editorButton}
                                variant="contained"
                                color="primary"
                                aria-label="clear"
                                onClick={handleCancel}
                                disabled={!zeekEditorPerms.write}
                            >
                                New Script
                            </Button>
                        </Box>
                        <Box mt={1} mr={1}>
                            <Button variant="contained" aria-label="back" onClick={handleGoBack}>Back</Button>
                        </Box>
                    </Box>
                </Box>
                <Box className={classes.messageBox} display={writable ? 'none' : 'flex'}>
                    <WarningIcon /> This script is not editable. Please duplicate to make changes.
                </Box>
                <Box className={classes.codeContainer} width="100%" height={height - (writable ? 250 : 270)} mx={1}>
                    <CodeMirror
                        editorDidMount={e => { setEditor(e) }}
                        value={selectedScript.content}
                        options={codemirrorOptions}
                        onBeforeChange={handleUpdateScript}
                    />
                </Box>
            </Box>
            <ConfirmationDialog
                onClose={handleOpenCreate}
                onOK={rename ? handleRename : handleCreate}
                open={openCreate}
                message={<Box px={3}>
                    <Validator
                        fullwidth
                        valueType="validNameCharacter"
                        type="text"
                        label="Script name"
                        value={selectedScript.name}
                        onChange={handleUpdateScriptName}
                    />
                    {rename && <Box mt={2}>
                        <Typography variant='subtitle2'>Any updates made to the script will also be saved when renaming.</Typography>
                    </Box>}
                </Box>}
                title={rename ? 'Rename script' : 'Create script'}
                cancelLabel="Cancel"
                okLabel="Yes"
                disableOK={!checkValidity(zeekScriptName.get())}
            />
            <ConfirmationDialog
                onClose={handleOpenDelete}
                onOK={handleDeleteScript}
                open={deleteOpen}
                message={`Are you sure you want to delete the script ${selectedScript.name}?`}
                title="Delete Script"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
        </main>
    );
};

export default ZeekEditor;
